import { Link } from 'react-router-dom';

import { ROUTES } from '../../../lib/config';
import { CheckboxField, Field, SegmentedControl, Select, TextField, Textarea } from '../../../components/ui/Field';
import { Button } from '../../../components/ui/Button';
import { Section } from '../../../components/ui/Panel';
import { CONTENT_TRANSFER_ENCODING_OPTIONS } from '../contentTransferEncoding';

/**
 * Sender identity, transfer encoding, recipients, Message-ID and rate — everything about *this* campaign
 * other than its body.
 *
 * Every field name here maps to a kebab-case key POST /send-email destructures; the
 * mapping lives in SEND_FIELD_NAMES rather than in this markup, so the form is free to
 * use readable state keys.
 */
export function CampaignSettingsCard({
  className,
  form,
  setField,
  limits,
  fieldError,
  isTestMode,
  rateInputsLocked,
  autoImapAvailable,
  autoImapChecked,
  autoImapBlockedReason,
  autoStartAvailable = false,
  autoStartChecked = false,
  onMessageIdHelp
}) {
  const rateLockTitle = rateInputsLocked
    ? 'Stop sending to change the rate. The running campaign already has this rate applied.'
    : undefined;

  return (
    <Section title="Mail Info" icon="fa-envelope-open-text" className={className}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <SegmentedControl
          name="test-bulk"
          label="Bulk or Test"
          value={form.testBulk}
          onChange={(value) => setField('testBulk', value)}
          options={[
            { value: 'Test', label: 'Test', icon: 'fa-flask' },
            { value: 'Bulk', label: 'Bulk', icon: 'fa-paper-plane' }
          ]}
        />
        <span title={autoImapBlockedReason}>
          <CheckboxField
            id="auto-imap-test"
            label="Auto IMAP Test"
            // The effective value, not the raw field: the box must never show armed while
            // the gate has made it inert.
            checked={autoImapChecked}
            onChange={(event) => setField('autoImapTest', event.target.checked)}
            disabled={!autoImapAvailable}
          />
        </span>
      </div>

      {/* Offered only when the server enables it, and only for bulk campaigns. */}
      {autoStartAvailable ? (
        <span title="If another campaign is using this SMTP account, queue this one and let the server start it as soon as the account is free — you can close the tab.">
          <CheckboxField
            id="auto-start"
            label="Start automatically when this SMTP account is free"
            checked={autoStartChecked}
            onChange={(event) => setField('autoStart', event.target.checked)}
          />
        </span>
      ) : null}

      <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-2">
        <TextField
          id="smtp-from-name"
          label="From Name"
          required
          placeholder="Enter From Name"
          autoComplete="off"
          value={form.fromName}
          onChange={(event) => setField('fromName', event.target.value)}
          error={fieldError('fromName')}
        />
        <TextField
          id="smtp-from-email"
          label="From Email"
          required
          placeholder="sender@yourdomain.com"
          autoComplete="off"
          value={form.fromEmail}
          onChange={(event) => setField('fromEmail', event.target.value)}
          error={fieldError('fromEmail')}
        />
      </div>

      <TextField
        id="subject"
        label="Subject"
        required
        placeholder="Enter Subject"
        autoComplete="off"
        value={form.subject}
        onChange={(event) => setField('subject', event.target.value)}
        error={fieldError('subject')}
      />

      <TextField
        id="return-path"
        type="email"
        label="Return-Path (optional)"
        placeholder="bounce@opterite.in"
        autoComplete="off"
        hint="Envelope sender (MAIL FROM) for bounces. Empty uses From Email; your SMTP provider may replace it."
        value={form.returnPath}
        onChange={(event) => setField('returnPath', event.target.value)}
        error={fieldError('returnPath')}
      />

      <Field
        label="Content Transfer Encoding"
        htmlFor="content-transfer-encoding"
        required
        error={fieldError('contentTransferEncoding')}
      >
        <Select
          id="content-transfer-encoding"
          value={form.contentTransferEncoding}
          onChange={(event) => setField('contentTransferEncoding', event.target.value)}
          aria-invalid={fieldError('contentTransferEncoding') ? true : undefined}
        >
          {CONTENT_TRANSFER_ENCODING_OPTIONS.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
      </Field>

      <TextField
        id="custom-message-id"
        label="Custom Message-ID"
        required
        labelAction={
          <Button
            variant="ghost"
            size="icon"
            icon="fa-circle-info"
            onClick={onMessageIdHelp}
            title="Message-ID placeholders"
            aria-label="Message-ID placeholders"
            className="-my-1 h-5 w-5 text-xs"
          />
        }
        className="[&_input]:font-mono [&_input]:text-xs"
        placeholder="<[[timestamp]]-[[bigchar(6)]]-[[num(6)]]@{{Domain}}>"
        autoComplete="off"
        spellCheck={false}
        value={form.customMessageId}
        onChange={(event) => setField('customMessageId', event.target.value)}
        error={fieldError('customMessageId')}
      />

      <Field
        label={
          <>
            Test Recipients{' '}
            {isTestMode ? (
              <span className="text-danger-500" aria-hidden="true">*</span>
            ) : (
              <span className="font-normal text-muted">(Test mode only)</span>
            )}
          </>
        }
        htmlFor="test-recp"
        error={fieldError('testRecipients')}
      >
        <Textarea
          id="test-recp"
          placeholder="one@example.com, two@example.com"
          value={form.testRecipients}
          onChange={(event) => setField('testRecipients', event.target.value)}
          // Disabled in Bulk mode, matching the old form. Harmless for submission: the
          // payload is built from state rather than from a FormData snapshot, so a
          // disabled control does not silently drop its value.
          disabled={!isTestMode}
          rows={3}
          spellCheck={false}
          className="font-mono text-xs leading-snug"
          aria-invalid={fieldError('testRecipients') ? true : undefined}
        />
      </Field>

      <TextField
        id="file-ids"
        label={
          <>
            File IDs{' '}
            {isTestMode ? (
              <span className="font-normal text-muted">(Bulk mode only)</span>
            ) : (
              <span className="text-danger-500" aria-hidden="true">*</span>
            )}
          </>
        }
        labelAction={
          <Link
            to={ROUTES.fileManager}
            className="-my-1 inline-flex items-center gap-1 rounded px-1 text-xs font-semibold text-brand-600 no-underline hover:bg-brand-50"
            title="Manage recipient files"
          >
            <i className="fa-solid fa-folder" aria-hidden="true" />
            Files
          </Link>
        }
        placeholder="e.g. 4f3c2a1b-…, 9d8e7f6a-…"
        autoComplete="off"
        spellCheck={false}
        value={form.fileIds}
        onChange={(event) => setField('fileIds', event.target.value)}
        error={fieldError('fileIds')}
      />

      <div className="grid min-w-0 grid-cols-3 gap-2">
        <TextField
          id="limit"
          label="Limit"
          required
          type="number"
          inputMode="numeric"
          min={limits.rateLimit.min}
          max={limits.rateLimit.max}
          step={1}
          placeholder="e.g. 35"
          value={form.limit}
          onChange={(event) => setField('limit', event.target.value)}
          error={fieldError('limit')}
          // readOnly, emphatically not disabled. The old form built its payload from a
          // FormData snapshot, where a disabled input is omitted entirely — so locking
          // these with `disabled` posted an empty `interval-seconds`, and an absent
          // interval is precisely how the server is told "no rate limit". That would have
          // silently converted the next batch of a paced campaign into an unpaced one
          // sending every remaining recipient as fast as SMTP allowed. This payload is
          // built from state so it is no longer load-bearing, but readOnly remains the
          // honest expression of "visible, submitted, not editable".
          readOnly={rateInputsLocked}
          aria-readonly={rateInputsLocked}
          title={rateLockTitle}
        />

        <TextField
          id="limit-to-send"
          label="Limit to Send"
          required
          type="number"
          inputMode="numeric"
          min={limits.limitToSend.min}
          max={limits.limitToSend.max}
          step={1}
          placeholder="e.g. 30000"
          value={form.limitToSend}
          onChange={(event) => setField('limitToSend', event.target.value)}
          error={fieldError('limitToSend')}
          readOnly={rateInputsLocked}
          aria-readonly={rateInputsLocked}
          title={rateInputsLocked ? 'Stop sending to change the campaign limit.' : undefined}
        />

        <TextField
          id="interval-seconds"
          label="Interval (s)"
          required
          type="number"
          inputMode="decimal"
          min={limits.rateIntervalSeconds.min}
          max={limits.rateIntervalSeconds.max}
          step="any"
          placeholder="e.g. 5"
          value={form.intervalSeconds}
          onChange={(event) => setField('intervalSeconds', event.target.value)}
          error={fieldError('intervalSeconds')}
          readOnly={rateInputsLocked}
          aria-readonly={rateInputsLocked}
          title={rateLockTitle}
        />
      </div>

      {rateInputsLocked ? (
        <p className="flex items-center gap-1.5 text-xs text-muted">
          <i className="fa-solid fa-lock" aria-hidden="true" />
          Rate is locked while this campaign is sending. Stop it to change Limit or Interval.
        </p>
      ) : null}
    </Section>
  );
}
